import { Injectable, inject } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { Observable, catchError, defer, from, map, of } from 'rxjs';
import { CslLocation } from '../models/csl-location.model';
import { PlacePrediction } from '../models/place-prediction.model';
import { ResidentialAddress } from '../models/residential-address.model';

@Injectable()
export class GoogleMapService {
  private readonly geocoder = inject(MapGeocoder);
  private readonly componentRestrictions = { country: 'us' };
  private sessionToken?: google.maps.places.AutocompleteSessionToken;

  getAddressFromCoordinates(lat: number, lng: number): Observable<ResidentialAddress | null> {
    return this.geocoder
      .geocode({
        location: { lat, lng },
        region: 'us',
      })
      .pipe(
        map(({ results, status }) => {
          /*
          return ADDRESS_MOCK;
          */

          if (status === 'OK' && results.length) {
            return this.getAddressFields(results[0]);
          }

          return null;
        }),
      );
  }

  getCoordinatesFromAddress(address: string): Observable<CslLocation | null> {
    return this.geocoder
      .geocode({
        address,
        region: 'us',
      })
      .pipe(
        map((response) => {
          if (response.status === 'OK' && response.results.length > 0) {
            const { lat, lng } = response.results[0].geometry.location;

            return {
              lat: lat(),
              lng: lng(),
            };
          }

          return null;
        }),
      );
  }

  async getPredictions(input: string, resultType?: PlacesTypes): Promise<PlacePrediction[]> {
    const types = resultType ? this.getGooglePlacesTypes(resultType) : undefined;
    const sessionToken = this.getOrCreateSessionToken();

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedPrimaryTypes: types,
      includedRegionCodes: [this.componentRestrictions.country],
      region: this.componentRestrictions.country,
      sessionToken,
    });

    return suggestions.flatMap(({ placePrediction }) =>
      placePrediction
        ? [
            {
              description: placePrediction.text.toString(),
              place: placePrediction.toPlace(),
            },
          ]
        : [],
    );
  }

  getPlaceDetails(place: google.maps.places.Place): Observable<google.maps.places.Place | null> {
    return defer(() => {
      this.resetSessionToken();

      return from(
        place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'adrFormatAddress', 'location'],
        }),
      ).pipe(
        map(({ place: detailedPlace }) => detailedPlace),
        catchError(() => of(null)),
      );
    });
  }

  private getOrCreateSessionToken(): google.maps.places.AutocompleteSessionToken {
    return (this.sessionToken ??= new google.maps.places.AutocompleteSessionToken());
  }

  private resetSessionToken(): void {
    this.sessionToken = undefined;
  }

  private getGooglePlacesTypes(placeType: PlacesTypes): string[] {
    switch (placeType) {
      case 'csl-cities': {
        return ['neighborhood', 'sublocality', 'locality', 'administrative_area_level_3'];
      }
      case 'address': {
        return ['street_address', 'premise', 'subpremise'];
      }
      default: {
        return [placeType];
      }
    }
  }

  placesAutocomplete(input: string, resultType?: PlacesTypes): Observable<PlacePrediction[]> {
    return defer(() => this.getPredictions(input, resultType));
  }

  getAddressFields(place: google.maps.places.Place | google.maps.GeocoderResult): ResidentialAddress {
    const addressComponents = 'addressComponents' in place ? place.addressComponents : place.address_components;
    const address = 'formattedAddress' in place ? place.formattedAddress : place.formatted_address;
    const addrHtml = 'adrFormatAddress' in place ? place.adrFormatAddress : undefined;
    const placeLocation = 'location' in place ? place.location : place.geometry.location;
    let street: string[] = [];
    let city = '';
    let state = '';
    let zip = '';
    let district = '';

    for (const component of addressComponents || []) {
      const types = component.types;
      const longName = 'longText' in component ? (component.longText ?? '') : component.long_name;
      const shortName = 'shortText' in component ? (component.shortText ?? longName) : component.short_name;
      if (types.includes('street_number')) {
        street = street.length === 0 ? [longName] : [longName, ...street];
      }

      if (types.includes('route')) {
        street = [...street, shortName];
      }

      if (
        !city /* 
          If we already found the city, stop to search it. 
          It is supposed that more detailed city name placed earlier in the array. 
         */ &&
        ['sublocality', 'locality', 'administrative_area_level_3'].some((city) => types.includes(city))
      ) {
        city = longName;
      }

      if (types.includes('neighborhood')) {
        district = shortName;
      }

      if (types.includes('administrative_area_level_1')) {
        state = shortName;
      }

      if (types.includes('postal_code')) {
        zip = longName;
      }
    }

    if (address && district) {
      const formattedStreet = addrHtml?.match(/<span class="street-address">(.+?)<\/span>/)?.[1] || '';
      const addressWithoutStreet = formattedStreet ? address.replace(formattedStreet, '') : address;
      const isDistrictInAddress = addressWithoutStreet.includes(district);
      if (isDistrictInAddress) {
        // District is specified in the address. Let's assume that the district is the city. (Bug #20472)
        city = district;
      }
    }

    return {
      street: street.join(' '),
      city,
      state,
      zip,
      ...(placeLocation && {
        geoLocation: {
          latitude: placeLocation.lat(),
          longitude: placeLocation.lng(),
        },
      }),
    };
  }

  getAddressText(address: ResidentialAddress | null): string {
    if (!address) return '';

    const { street, city, state, zip } = address;

    return [street, city, state, zip].join(', ');
  }
}

type GooglePlacesTypes = 'address' | 'postal_code';
type PlacesTypes = GooglePlacesTypes | 'csl-cities';
